import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";

import {
  createMentionElement,
  getActiveMention,
  getCaretOffset,
  getPopupPosition,
  getRangeForOffsets,
  readValue,
  readVisibleText
} from "./utils";

import type {
  ActiveMention,
  MentionOption,
  MentionPopupPosition
} from "./types";

type UseMentionSuggestionsParams = {
  editorRef: RefObject<HTMLDivElement>;
  mentions: MentionOption[];
  mask?: string;
  maxDropdownHeight: number;
  maxSuggestions: number;
  disabled?: boolean;
  readOnly?: boolean;
  onChange: (value: string) => void;
  onMentionSelect?: (mention: MentionOption) => void;
};

const useMentionSuggestions = (params: UseMentionSuggestionsParams) => {
  const {
    editorRef,
    mentions,
    mask,
    maxDropdownHeight,
    maxSuggestions,
    disabled,
    readOnly,
    onChange,
    onMentionSelect
  } = params;

  const popupHeightRef = useRef(maxDropdownHeight);
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(
    null
  );
  const [popupPosition, setPopupPosition] =
    useState<MentionPopupPosition | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const filteredMentions = useMemo(() => {
    if (!activeMention) {
      return [];
    }

    const query = activeMention.query.toLocaleLowerCase();

    return mentions
      .filter(mention => {
        const searchableText = [mention.label, ...(mention.keywords || [])]
          .join(" ")
          .toLocaleLowerCase();

        return searchableText.includes(query);
      })
      .slice(0, maxSuggestions);
  }, [activeMention, maxSuggestions, mentions]);

  const closeSuggestions = useCallback(() => {
    setActiveMention(null);
    setPopupPosition(null);
    setHighlightedIndex(-1);
  }, []);

  const updateSuggestions = useCallback(() => {
    const editor = editorRef.current;

    if (!editor || disabled || readOnly) {
      closeSuggestions();
      return;
    }

    const caretOffset = getCaretOffset(editor);

    if (caretOffset === null) {
      closeSuggestions();
      return;
    }

    const mention = getActiveMention(readVisibleText(editor), caretOffset);

    if (!mention) {
      closeSuggestions();
      return;
    }

    setActiveMention(mention);
    setPopupPosition(getPopupPosition(editor, popupHeightRef.current));
  }, [closeSuggestions, disabled, editorRef, readOnly]);

  const handlePopupHeightChange = useCallback(
    (height: number) => {
      const editor = editorRef.current;

      popupHeightRef.current = height;

      if (editor) {
        setPopupPosition(getPopupPosition(editor, height));
      }
    },
    [editorRef]
  );

  const selectMention = useCallback(
    (mention: MentionOption) => {
      const editor = editorRef.current;

      if (!editor || !activeMention || mention.disabled) {
        return;
      }

      const range = getRangeForOffsets(
        editor,
        activeMention.start,
        activeMention.end
      );

      if (!range) {
        return;
      }

      const suffix = readVisibleText(editor).slice(activeMention.end);
      const mentionElement = createMentionElement(mention, mask);
      const trailingSpace = suffix.startsWith(" ")
        ? null
        : document.createTextNode(" ");
      const fragment = document.createDocumentFragment();

      fragment.append(mentionElement);

      if (trailingSpace) {
        fragment.append(trailingSpace);
      }

      range.deleteContents();
      range.insertNode(fragment);

      const selection = window.getSelection();
      const caretRange = document.createRange();
      const followingNode = mentionElement.nextSibling;

      if (
        !trailingSpace &&
        followingNode?.nodeType === Node.TEXT_NODE &&
        followingNode.textContent?.startsWith(" ")
      ) {
        caretRange.setStart(followingNode, 1);
      } else {
        caretRange.setStartAfter(trailingSpace || mentionElement);
      }

      caretRange.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(caretRange);

      onChange(readValue(editor));
      onMentionSelect?.(mention);
      closeSuggestions();
      editor.focus();
    },
    [
      activeMention,
      closeSuggestions,
      editorRef,
      mask,
      onChange,
      onMentionSelect
    ]
  );

  useEffect(() => {
    const firstEnabledIndex = filteredMentions.findIndex(
      mention => !mention.disabled
    );
    setHighlightedIndex(firstEnabledIndex);
  }, [activeMention?.query, filteredMentions]);

  useEffect(() => {
    if (!activeMention) {
      return;
    }

    const handleViewportChange = () => updateSuggestions();

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [activeMention, updateSuggestions]);

  return {
    activeMention,
    popupPosition,
    filteredMentions,
    highlightedIndex,
    setHighlightedIndex,
    closeSuggestions,
    updateSuggestions,
    handlePopupHeightChange,
    selectMention
  };
};

export default useMentionSuggestions;
